/* ==========================================================================
   VIP Photobook — lightbox + forced image download

   Why the download is a fetch and not just <a download>: the `download`
   attribute is ignored cross-origin, and images are served from
   cdn.shopify.com. So a plain link would open the photo in a tab instead of
   saving it. Fetching to a blob and handing the browser a same-origin object
   URL makes `download` authoritative again. Shopify's CDN sends
   Access-Control-Allow-Origin: *, so the fetch is permitted — but there is a
   new-tab fallback in case that ever changes.

   The zip (custom.photobook) needs none of this: browsers can't render a
   .zip, so it saves regardless of origin.
   ========================================================================== */

(function () {
  'use strict';

  /* Sticky bar: shrink the logo once the fan starts scrolling, so a 200px+ bar
     doesn't sit on a quarter of the viewport for the whole book.

     Runs before the lightbox's early returns below so it still works on a
     zip-only page that has no tiles. The threshold is well clear of the height
     the bar loses, so the resulting reflow can't re-trigger the toggle and
     oscillate. */
  (function () {
    var bar = document.querySelector('.vpb__bar');
    if (!bar) return;

    var THRESHOLD = 60;
    var queued = false;

    function apply() {
      queued = false;
      bar.classList.toggle('is-scrolled', window.pageYOffset > THRESHOLD);
    }

    window.addEventListener(
      'scroll',
      function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(apply);
      },
      { passive: true }
    );

    apply(); // a reload can restore a scrolled position
  })();

  var root = document.querySelector('[data-vpb]');
  var lb = document.querySelector('[data-vpb-lightbox]');
  if (!root || !lb) return;

  var tiles = Array.from(root.querySelectorAll('[data-vpb-tile]'));
  if (!tiles.length) return;

  var stage = lb.querySelector('[data-vpb-stage]');
  var counter = lb.querySelector('[data-vpb-counter]');
  var dims = lb.querySelector('[data-vpb-dims]');
  var dlBtn = lb.querySelector('[data-vpb-lb-download]');
  var closeBtn = lb.querySelector('[data-vpb-close]');
  var prevBtn = lb.querySelector('[data-vpb-prev]');
  var nextBtn = lb.querySelector('[data-vpb-next]');

  var current = -1;
  var lastFocused = null;

  var items = tiles.map(function (t) {
    var img = t.querySelector('img');
    return {
      full: t.getAttribute('data-full'),
      dims: t.getAttribute('data-dims') || '',
      alt: img ? img.getAttribute('alt') || '' : '',
      tile: t
    };
  });

  function filenameFrom(url) {
    try {
      var path = new URL(url, location.href).pathname;
      var name = path.split('/').pop() || 'photo.jpg';
      return decodeURIComponent(name);
    } catch (e) {
      return 'photo.jpg';
    }
  }

  /* Preload neighbours so arrowing through the book doesn't flash. */
  function preload(i) {
    [i - 1, i + 1].forEach(function (n) {
      if (n < 0 || n >= items.length) return;
      var p = new Image();
      p.src = items[n].full;
    });
  }

  function render(i) {
    var item = items[i];
    if (!item) return;
    current = i;

    stage.innerHTML = '';
    var img = document.createElement('img');
    img.src = item.full;
    img.alt = item.alt;
    stage.appendChild(img);

    counter.textContent = i + 1 + ' / ' + items.length;
    dims.textContent = item.dims;
    dlBtn.setAttribute('href', item.full);
    dlBtn.setAttribute('download', filenameFrom(item.full));

    prevBtn.disabled = i === 0;
    nextBtn.disabled = i === items.length - 1;

    preload(i);
  }

  function open(i) {
    lastFocused = document.activeElement;
    lb.removeAttribute('hidden');
    document.documentElement.style.overflow = 'hidden';
    render(i);
    closeBtn.focus();
  }

  function close() {
    lb.setAttribute('hidden', '');
    document.documentElement.style.overflow = '';
    stage.innerHTML = '';
    // Return focus to the tile that was opened, not the top of the document.
    var target = lastFocused || (items[current] && items[current].tile);
    if (target && target.focus) target.focus();
    current = -1;
  }

  function step(delta) {
    var next = current + delta;
    if (next < 0 || next >= items.length) return;
    render(next);
  }

  tiles.forEach(function (t, i) {
    t.addEventListener('click', function () {
      open(i);
    });
  });

  closeBtn.addEventListener('click', close);
  prevBtn.addEventListener('click', function () { step(-1); });
  nextBtn.addEventListener('click', function () { step(1); });

  /* Click the backdrop (but not the image or the chrome) to dismiss. */
  lb.addEventListener('click', function (e) {
    if (e.target === lb || e.target === stage) close();
  });

  document.addEventListener('keydown', function (e) {
    if (lb.hasAttribute('hidden')) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
  });

  dlBtn.addEventListener('click', function (e) {
    var url = dlBtn.getAttribute('href');
    if (!url || url === '#') return;

    e.preventDefault();
    dlBtn.setAttribute('aria-busy', 'true');

    fetch(url, { mode: 'cors', credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.blob();
      })
      .then(function (blob) {
        var objUrl = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = objUrl;
        a.download = filenameFrom(url);
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Revoke on the next tick; revoking synchronously can cancel the save.
        setTimeout(function () { URL.revokeObjectURL(objUrl); }, 1000);
      })
      .catch(function (err) {
        // CORS or network failure — at least give them the full-size image.
        console.warn('[vip-photobook] download fell back to new tab:', err);
        window.open(url, '_blank', 'noopener');
      })
      .then(function () {
        dlBtn.removeAttribute('aria-busy');
      });
  });
})();
